import { Enquiry } from "../../components/Enquiry";
import { Footer } from "../../components/Footer";
import { Layout } from "../../components/Layout";
import { CTA_LINK, CTA_TEXT, DEMO_TEXT } from "@/components/layout/PageLayout";
import { FeatureShowcase } from "@/components/layout/sections/FeatureShowcase";
import { Hero } from "@/components/layout/sections/Hero";
import ImageSection from "@/components/layout/sections/ImageSection";
import { ProductFooter } from "@/components/layout/sections/ProductFooter";
import Section from "@/components/layout/sections/Section";
import TitleSection from "@/components/layout/text/TitleSection/TitleSection";
import { faUnsplash } from "@fortawesome/free-brands-svg-icons";
import {
  faAsterisk,
  faBlockQuote,
  faChartColumn,
  faCode,
  faCube,
  faDroplet,
  faFontCase,
  faLoader,
  faPhotoFilm,
  faPuzzlePiece,
  faRectangleWide,
  faShapes,
  faTable,
  faTextSize,
  faVideo,
} from "@icons/regular";
import { Button, Icon } from "@ui";
import { Link, graphql } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";
import * as React from "react";
import * as ReactDOM from "react-dom";
import { Helmet } from "react-helmet";

const editorFeatures = [
  {
    id: 1,
    description: "Traditional text formatting",
    icon: faFontCase,
  },
  {
    id: 2,
    description: "Drop caps and block quotes",
    icon: faBlockQuote,
  },
  {
    id: 3,
    description: "Resize text in all sections",
    icon: faTextSize,
  },
  {
    id: 4,
    description: "Annotations and footnotes",
    icon: faAsterisk,
  },
  {
    id: 5,
    description: "Media library",
    icon: faPhotoFilm,
  },
  {
    id: 6,
    description: "Unsplash integration",
    icon: faUnsplash,
  },
  {
    id: 7,
    description: "Rich embeds",
    icon: faPuzzlePiece,
  },
  {
    id: 8,
    description: "Video transcoding",
    icon: faVideo,
  },
  {
    id: 9,
    description: "Image filters and enhancements",
    icon: faDroplet,
  },
  {
    id: 10,
    description: "Foreground animations",
    icon: faLoader,
  },
  {
    id: 11,
    description: "Shapes and corners",
    icon: faShapes,
  },
  {
    id: 12,
    description: "3D images and effects",
    icon: faCube,
  },
  {
    id: 13,
    description: "Tables",
    icon: faTable,
  },
  {
    id: 14,
    description: "Charts",
    icon: faChartColumn,
  },
  {
    id: 15,
    description: "Buttons",
    icon: faRectangleWide,
  },
  {
    id: 16,
    description: "Custom HTML",
    icon: faCode,
  },
];

const previewFeatures = [
  {
    id: 1,
    title: "Share",
    description:
      "Whether or not your team members and stakeholders are on Shorthand, you can securely share and collaborate throughout the creation process.",
  },
  {
    id: 2,
    title: "Preview",
    description: "Preview your story across mobile, tablet, and desktop modes to ensure a flawless experience for every audience.",
  },
  {
    id: 3,
    title: "Publish",
    description: (
      <>
        Happy with your story? Share it with the world — publishing is just a click away.{" "}
        <Link to="/features/publishing-and-hosting/">Learn more</Link>.
      </>
    ),
  },
];

const FeaturesEditorAndLayout: React.FC<{ location: any }> = props => {
  const [showPortal, setShowPortal] = React.useState(false);
  const prePopulatedFields = React.useRef<Record<any, string>>({});

  function handleShowPortal(fields?: Record<any, string>): void {
    setShowPortal(true);
    prePopulatedFields.current = fields || {};
  }

  const portalProps = {
    type: "general-enquiry",
    subject: "general-enquiry",
    prePopulatedFields: prePopulatedFields.current,
    onClose: () => {
      setShowPortal(false);
      prePopulatedFields.current = {};
    },
  };

  return (
    <Layout location={props.location} theme="dark-theme">
      {showPortal && ReactDOM.createPortal(<Enquiry {...portalProps} />, document.getElementById("modal"))}
      <main id="main" className="marketing-content container-fluid">
        <Helmet
          bodyAttributes={{
            class: "body-shorthand body-marketing body-marketing-secondary body-marketing-features body-light hide-elevio",
          }}
        ></Helmet>
        <Hero
          title={
            <>
              Built for speed. Designed for <span className="tw-font-PassengerSerif tw-italic">creativity</span>.
            </>
          }
          description="Enjoy complete creative control with an intuitive drag-and-drop editor. Spark your process with AI-powered layouts and ready-made templates."
          image={
            <StaticImage
              src={`../../../static/product/layout-and-editor/layout-and-editor-hero.png`}
              alt="App screenshot showing the Shorthand editor with a layout suggestion"
              width={2432}
              height={1442}
              className="tw-w-[76rem] tw-rounded-md tw-bg-white/5 tw-shadow-2xl tw-ring-1 tw-ring-white/10"
            />
          }
          actions={
            <>
              <Button asChild variant="primary-gradient" size="lg">
                <a href={CTA_LINK} className="tw-no-underline hover:tw-no-underline">
                  {CTA_TEXT}
                </a>
              </Button>
              <Button
                variant="secondary-outline"
                style={{ border: "1px solid white" }}
                size="lg"
                onClick={() =>
                  handleShowPortal({
                    interestedIn: "demo",
                    message: "booking a demo of the Shorthand platform",
                  })
                }
              >
                {DEMO_TEXT}
              </Button>
            </>
          }
        />
        <Section>
          <FeatureShowcase
            preventOverflow
            title={<>Powerful. Intuitive. Almost zero-learning-curve.</>}
            description={
              <div className="tw-space-y-6">
                <div>The Shorthand editor empowers non-tech users to craft beautiful stories — in record time.</div>
                <div>
                  With flexible controls to tailor text and images, switch layouts and modes, and add a little magic to your story, you
                  can build what you want seamlessly. All while staying on brand.
                </div>
              </div>
            }
            image="product/layout-and-editor/editor-screenshot.png"
            imagePlacement="left"
          />
        </Section>
        <Section>
          <TitleSection
            headingElement="h2"
            title={
              <>
                Creative <span className="tw-font-PassengerSerif tw-italic">power</span> in your hands.
              </>
            }
            description="Everything you need to bring your vision to life."
          />
          <div className="tw-mx-auto tw-mt-10 tw-grid tw-grid-cols-2 tw-items-center tw-gap-x-2 tw-gap-y-12 sm:tw-mt-16 sm:tw-grid-cols-4 sm:tw-gap-x-8 sm:tw-gap-y-14">
            {editorFeatures.map(feature => (
              <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4" key={`editor-feature-${feature.id}`}>
                <Icon icon={feature.icon} className="tw-size-6 tw-text-[var(--foreground-color)] sm:tw-size-7 lg:tw-size-8" />
                <span className="tw-max-w-[15ch] tw-text-center tw-text-[var(--foreground-color-muted)]">{feature.description}</span>
              </div>
            ))}
          </div>
        </Section>
        <Section>
          <TitleSection
            headingElement="h2"
            title={
              <>
                <span className="tw-font-PassengerSerif tw-italic">Preview & collaborate</span>
              </>
            }
            description="See your story come to life in real time. Switch between the editor and preview to view your final product as you work — and tweak it to perfection."
          />
          <ImageSection
            image="product/layout-and-editor/editor-preview.png"
            imageAlt="Editor screenshot showing the story preview with mobile view selected"
          >
            <div className="tw-mt-16 tw-grid tw-grid-cols-3 tw-gap-4">
              {previewFeatures.map(feature => (
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-start tw-gap-4" key={`preview-feature-${feature.id}`}>
                  <h3 className="tw-text-center !tw-text-lg tw-font-semibold tw-uppercase tw-tracking-tight tw-text-brand_scale-400">
                    {feature.title}
                  </h3>
                  <span className="tw-max-w-[30ch] tw-text-center tw-text-base/6 tw-text-[var(--foreground-color)]">
                    {feature.description}
                  </span>
                </div>
              ))}
            </div>
          </ImageSection>
        </Section>
        <Section>
          <FeatureShowcase
            preventOverflow
            title={
              <>
                AI that designs <span className="tw-font-PassengerSerif tw-italic">with you</span>
              </>
            }
            description={
              <div className="tw-space-y-6">
                <div>
                  Need a fresh take? Shorthand’s AI suggestions can help you explore different section arrangements and layouts, giving
                  you a head start on crafting immersive experiences.
                </div>
                <div>
                  Upload existing content — <strong>from the web or PDF files</strong> — or <strong>start from a prompt</strong>, and
                  let our AI auto-layout tool structure your story beautifully in seconds.
                </div>
              </div>
            }
            image="product/layout-and-editor/choose-layout.png"
            imagePlacement="right"
          />
        </Section>
        <Section>
          <FeatureShowcase
            preventOverflow
            title={
              <>
                Templates to <span className="tw-font-PassengerSerif tw-italic">inspire</span>
              </>
            }
            description={
              <div className="tw-space-y-6">
                <div>
                  Jumpstart your next story with a range of inspiring, professionally crafted templates. Get ideas for brand
                  publications, features, reports, digital magazines, and more. <Link to="/templates">Explore templates.</Link>
                </div>
              </div>
            }
            image="product/layout-and-editor/choose-template.png"
            imagePlacement="left"
          />
        </Section>

        <ProductFooter />
        <Footer className="mt-0" />
      </main>
    </Layout>
  );
};

export default FeaturesEditorAndLayout;

export const query = graphql`
  query FeaturesSections {
    site {
      siteMetadata {
        name
        title
        tagline
        description
      }
    }
    allFeaturedStoriesJson {
      nodes {
        title
        linkUrl
        imageUrl {
          alt
          src {
            childImageSharp {
              gatsbyImageData(placeholder: BLURRED, width: 600, height: 830, quality: 80)
            }
            publicURL
          }
        }
      }
    }
  }
`;
